import { Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import icClose from '@iconify/icons-ic/twotone-close';
import { PurchaseActorScopeEnum } from '../../../models/purchase-actor.model';

/** Choix d'application d'un enregistrement sur une famille non-feuille (calque de la question
 * `propagationMode` de la modale Supervision métier, adaptée aux sous-familles). */
export type PurchaseActorApplyChoice = 'all' | 'onlyDefault';

export interface PurchaseActorApplyModalData {
  nodeLabel: string;
  childrenCount: number;
  scope: PurchaseActorScopeEnum;
}

@Component({
  selector: 'purchase-actor-apply-modal',
  // Même remède que default-supervision-assign-modal : les radios legacy mettent le label
  // en nowrap, ce qui fait déborder la modale (scrolls) au lieu de replier le texte.
  styles: [
    `
      .pa-apply-options ::ng-deep .mat-radio-label {
        white-space: normal;
        align-items: flex-start;
      }
    `,
  ],
  template: `
    <div [formGroup]="form">
      <div class="flex items-center" mat-dialog-title>
        <h2 class="headline m-0 flex-auto">Enregistrer la configuration</h2>
        <button class="text-secondary" mat-dialog-close type="button" mat-icon-button>
          <ic-icon [icon]="icClose"></ic-icon>
        </button>
      </div>

      <mat-dialog-content>
        <p class="mb-3">
          Vous venez de modifier les acteurs de la famille
          <strong>{{ data.nodeLabel }}</strong>
          . Comment souhaitez-vous appliquer la modification aux {{ data.childrenCount }} sous-familles ?
        </p>

        <mat-radio-group formControlName="choice" class="flex flex-col gap-3 pa-apply-options">
          <mat-radio-button value="onlyDefault">
            <span class="block font-medium">Appliquer uniquement aux sous-familles par défaut.</span>
            <span class="block text-secondary text-xs">
              Les sous-familles surchargées manuellement conservent leurs acteurs actuels.
            </span>
          </mat-radio-button>
          <mat-radio-button value="all">
            <span class="block font-medium">
              Remplacer les acteurs sur toutes les sous-familles, y compris celles qui ont été modifiées.
            </span>
            <span class="block text-secondary text-xs">
              Les surcharges posées manuellement sur les sous-familles seront réinitialisées : elles hériteront de
              cette configuration.
            </span>
            <!-- Niveau client : la réinitialisation ne porte que sur ce niveau (clearChildrenOverrides filtre le scope). -->
            <span *ngIf="data.scope === PurchaseActorScopeEnum.TENANT" class="block text-secondary text-xs mt-1">
              Les configurations des établissements ne sont pas modifiées.
            </span>
          </mat-radio-button>
        </mat-radio-group>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close type="button">Annuler</button>
        <button mat-raised-button color="primary" type="button" [disabled]="form.invalid" (click)="confirm()">
          Enregistrer
        </button>
      </mat-dialog-actions>
    </div>
  `,
})
export class PurchaseActorApplyModalComponent {
  icClose = icClose;
  readonly PurchaseActorScopeEnum = PurchaseActorScopeEnum;
  form = new UntypedFormGroup({
    choice: new UntypedFormControl('onlyDefault', [Validators.required]),
  });

  constructor(
    private readonly dialogRef: MatDialogRef<PurchaseActorApplyModalComponent, PurchaseActorApplyChoice>,
    @Inject(MAT_DIALOG_DATA) public readonly data: PurchaseActorApplyModalData,
  ) {}

  confirm(): void {
    this.dialogRef.close(this.form.value.choice as PurchaseActorApplyChoice);
  }
}
